/**
 * DojoOS standard modules catalog.
 * @typedef {object} DojoOsModule
 * @property {string} id
 * @property {string} labelFr
 * @property {string} labelEn
 * @property {string} icon
 * @property {'phase1'|'phase2'|'future'|'advanced'} phase
 * @property {string[]} roles
 * @property {string} descriptionFr
 * @property {string} descriptionEn
 * @property {string} [navGroupId]
 * @property {string} [routeKey]
 */

/** @type {DojoOsModule[]} */
export const DOJOOS_MODULES = [
  {
    id: "dashboard",
    labelFr: "Tableau de bord",
    labelEn: "Dashboard",
    icon: "📊",
    phase: "phase1",
    roles: ["admin", "manager", "player"],
    descriptionFr: "Vue d'ensemble par rôle.",
    descriptionEn: "Role overview dashboard.",
    navGroupId: "admin",
    routeKey: "admin.dashboard",
  },
  {
    id: "organizations",
    labelFr: "Organisations",
    labelEn: "Organizations",
    icon: "🏢",
    phase: "phase2",
    roles: ["admin", "federation"],
    descriptionFr: "Structure multi-dojo / fédération.",
    descriptionEn: "Multi-dojo / federation structure.",
    navGroupId: "admin",
    routeKey: "admin.organizations",
  },
  {
    id: "dojos",
    labelFr: "Dojos",
    labelEn: "Dojos",
    icon: "🏯",
    phase: "phase1",
    roles: ["admin", "manager", "player", "federation"],
    descriptionFr: "Fiches dojo, adresse, affiliation.",
    descriptionEn: "Dojo records, address, affiliation.",
    navGroupId: "admin",
    routeKey: "admin.dojos",
  },
  {
    id: "instructors",
    labelFr: "Instructeurs / Sensei",
    labelEn: "Instructors",
    icon: "👊",
    phase: "phase1",
    roles: ["admin", "federation"],
    descriptionFr: "Sensei, grades, certifications.",
    descriptionEn: "Sensei, grades, certifications.",
    navGroupId: "admin",
    routeKey: "admin.instructors",
  },
  {
    id: "assistants",
    labelFr: "Assistants / Sempai",
    labelEn: "Assistants / Sempai",
    icon: "🤝",
    phase: "phase2",
    roles: ["admin", "assistant"],
    descriptionFr: "Sempai et autorisations par groupe.",
    descriptionEn: "Sempai and group permissions.",
    navGroupId: "admin",
    routeKey: "admin.assistants",
  },
  {
    id: "students",
    labelFr: "Étudiants",
    labelEn: "Students",
    icon: "🎓",
    phase: "phase1",
    roles: ["admin", "manager", "assistant", "parent"],
    descriptionFr: "Profils élèves, groupes, ceintures.",
    descriptionEn: "Student profiles, groups, belts.",
    navGroupId: "admin",
    routeKey: "admin.students",
  },
  {
    id: "parents",
    labelFr: "Parents",
    labelEn: "Parents",
    icon: "👨‍👩‍👧",
    phase: "phase2",
    roles: ["admin", "parent"],
    descriptionFr: "Liens parent-enfant, communications.",
    descriptionEn: "Parent-child links, communications.",
    navGroupId: "admin",
    routeKey: "admin.parents",
  },
  {
    id: "classes",
    labelFr: "Classes / cours",
    labelEn: "Classes",
    icon: "📚",
    phase: "phase2",
    roles: ["admin", "manager", "assistant", "player"],
    descriptionFr: "Groupes, niveaux, horaires hebdomadaires.",
    descriptionEn: "Groups, levels, weekly schedule.",
    navGroupId: "admin",
    routeKey: "admin.classes",
  },
  {
    id: "schedule",
    labelFr: "Horaire",
    labelEn: "Schedule",
    icon: "🗓️",
    phase: "phase2",
    roles: ["manager", "parent", "player"],
    descriptionFr: "Planning des cours par groupe.",
    descriptionEn: "Class schedule by group.",
    routeKey: "player.schedule",
  },
  {
    id: "attendance",
    labelFr: "Présences",
    labelEn: "Attendance",
    icon: "✅",
    phase: "phase2",
    roles: ["manager", "assistant"],
    descriptionFr: "Check-in cours — suivi léger Phase 2.",
    descriptionEn: "Class check-in — light tracking Phase 2.",
    routeKey: "manager.attendance",
  },
  {
    id: "belts",
    labelFr: "Ceintures / grades",
    labelEn: "Belts / grades",
    icon: "🎗️",
    phase: "phase2",
    roles: ["admin", "manager", "federation"],
    descriptionFr: "Système de progression par ceintures.",
    descriptionEn: "Belt progression system.",
    routeKey: "admin.belts",
  },
  {
    id: "progress",
    labelFr: "Progression",
    labelEn: "Progress",
    icon: "📈",
    phase: "phase2",
    roles: ["manager", "player", "parent"],
    descriptionFr: "Techniques, kata, kumite, discipline.",
    descriptionEn: "Techniques, kata, kumite, discipline.",
    routeKey: "manager.progress",
  },
  {
    id: "exams",
    labelFr: "Examens",
    labelEn: "Exams",
    icon: "📝",
    phase: "future",
    roles: ["admin", "manager"],
    descriptionFr: "Inscription, passage, validation grade.",
    descriptionEn: "Registration, grading, validation.",
    routeKey: "manager.exams",
  },
  {
    id: "events",
    labelFr: "Événements",
    labelEn: "Events",
    icon: "📅",
    phase: "phase2",
    roles: ["admin", "manager", "player", "parent"],
    descriptionFr: "Séminaires, stages, camps, démonstrations.",
    descriptionEn: "Seminars, camps, demos.",
    navGroupId: "admin",
    routeKey: "admin.events",
  },
  {
    id: "competitions",
    labelFr: "Compétitions",
    labelEn: "Competitions",
    icon: "🏆",
    phase: "future",
    roles: ["admin", "federation", "manager"],
    descriptionFr: "Inscriptions et résultats compétitifs.",
    descriptionEn: "Competition entries and results.",
    routeKey: "admin.competitions",
  },
  {
    id: "documents",
    labelFr: "Documents",
    labelEn: "Documents",
    icon: "📄",
    phase: "phase1",
    roles: ["admin", "manager", "assistant", "player", "parent"],
    descriptionFr: "Protocoles, règlements, ressources.",
    descriptionEn: "Protocols, rules, resources.",
    navGroupId: "admin",
    routeKey: "admin.documents",
  },
  {
    id: "communications",
    labelFr: "Communications",
    labelEn: "Communications",
    icon: "📣",
    phase: "phase2",
    roles: ["admin", "manager", "parent"],
    descriptionFr: "Messages aux familles et groupes.",
    descriptionEn: "Messages to families and groups.",
    navGroupId: "admin",
    routeKey: "admin.communications",
  },
  {
    id: "billing",
    labelFr: "Paiements",
    labelEn: "Billing",
    icon: "💳",
    phase: "advanced",
    roles: ["admin"],
    descriptionFr: "Abonnements, examens — futur.",
    descriptionEn: "Subscriptions, exams — future.",
    routeKey: "admin.billing",
  },
  {
    id: "reports",
    labelFr: "Rapports / analytics",
    labelEn: "Reports",
    icon: "📊",
    phase: "future",
    roles: ["admin", "federation"],
    descriptionFr: "Statistiques multi-dojo.",
    descriptionEn: "Multi-dojo analytics.",
    routeKey: "admin.reports",
  },
  {
    id: "settings",
    labelFr: "Comptes / rôles",
    labelEn: "Accounts / roles",
    icon: "🔑",
    phase: "phase1",
    roles: ["admin"],
    descriptionFr: "Comptes, rôles, demandes d'accès.",
    descriptionEn: "Accounts, roles, access requests.",
    navGroupId: "admin",
    routeKey: "admin.accounts",
  },
  {
    id: "help",
    labelFr: "Aide",
    labelEn: "Help",
    icon: "❓",
    phase: "phase1",
    roles: ["admin", "manager", "assistant", "player", "parent", "guest"],
    descriptionFr: "Aide contextuelle GSD.",
    descriptionEn: "GSD contextual help.",
    routeKey: "common.help",
  },
  {
    id: "training",
    labelFr: "Formation",
    labelEn: "Training",
    icon: "🎓",
    phase: "phase1",
    roles: ["admin", "manager", "assistant", "player", "parent"],
    descriptionFr: "Parcours formation par rôle.",
    descriptionEn: "Role-based training tracks.",
    routeKey: "common.training",
  },
];

/**
 * @param {string} moduleId
 * @returns {DojoOsModule|undefined}
 */
export function findDojoOsModule(moduleId) {
  return DOJOOS_MODULES.find((m) => m.id === moduleId);
}

/**
 * @param {string[]} [enabledModules]
 * @returns {DojoOsModule[]}
 */
export function filterDojoOsModules(enabledModules) {
  if (!enabledModules?.length) return DOJOOS_MODULES;
  const set = new Set(enabledModules);
  return DOJOOS_MODULES.filter((m) => set.has(m.id));
}

/**
 * @param {string} roleId
 * @param {string[]} [enabledModules]
 * @returns {DojoOsModule[]}
 */
export function getModulesForRole(roleId, enabledModules) {
  const pool = filterDojoOsModules(enabledModules);
  return pool.filter((m) => m.roles.includes(roleId));
}

/**
 * @param {'phase1'|'phase2'|'future'|'advanced'} phase
 * @param {'fr'|'en'|string} [locale]
 */
export function getModulePhaseLabel(phase, locale = "fr") {
  const map = {
    fr: { phase1: "Phase 1", phase2: "Phase 2", future: "Futur", advanced: "Avancé" },
    en: { phase1: "Phase 1", phase2: "Phase 2", future: "Future", advanced: "Advanced" },
  };
  const lang = locale === "en" ? "en" : "fr";
  return map[lang][phase] || phase;
}
